import { useEffect, useRef } from 'react';
import gsap from 'gsap';

export default function Hero() {
  const sectionRef = useRef<HTMLElement>(null);
  const title1Ref = useRef<HTMLDivElement>(null);
  const title2Ref = useRef<HTMLDivElement>(null);
  const subtitleRef = useRef<HTMLParagraphElement>(null);
  const videoRef = useRef<HTMLVideoElement>(null);

  useEffect(() => {
    // Entrance animations
    const tl = gsap.timeline({ delay: 0.5 });
    tl.fromTo(title1Ref.current, { opacity: 0, y: 40 }, { opacity: 1, y: 0, duration: 1, ease: 'power3.out' })
      .fromTo(title2Ref.current, { opacity: 0, y: 40 }, { opacity: 1, y: 0, duration: 1, ease: 'power3.out' }, '-=0.6')
      .fromTo(subtitleRef.current, { opacity: 0, y: 20 }, { opacity: 1, y: 0, duration: 0.8, ease: 'power3.out' }, '-=0.5');

    // Ensure video plays
    if (videoRef.current) {
      videoRef.current.play().catch(() => {});
    }
  }, []);

  return (
    <section
      id="hero"
      ref={sectionRef}
      className="relative w-full min-h-screen flex items-center justify-center overflow-hidden"
    >
      {/* Video Background */}
      <video
        ref={videoRef}
        autoPlay
        muted
        loop
        playsInline
        className="absolute inset-0 w-full h-full object-cover"
        style={{ zIndex: 0, filter: 'brightness(0.6) saturate(1.2)' }}
      >
        <source src="/assets/verixa-hero10mb.mp4" type="video/mp4" />
      </video>

      {/* Gradient Overlay */}
      <div
        className="absolute inset-0"
        style={{
          zIndex: 1,
          background: 'radial-gradient(ellipse 80% 60% at 50% 40%, rgba(5,5,5,0.3) 0%, rgba(5,5,5,0.7) 70%, rgba(5,5,5,0.95) 100%)',
        }}
      />

      {/* Content */}
      <div className="relative text-center px-6" style={{ zIndex: 2 }}>
        <div
          ref={title1Ref}
          className="font-syncopate text-white uppercase leading-[1.05] tracking-[0.02em]"
          style={{
            fontSize: 'clamp(1.8rem, 5vw, 4.5rem)',
            textShadow: '0 0 60px rgba(122,0,255,0.3), 0 0 120px rgba(0,240,255,0.15)',
          }}
        >
          EU NÃO CRIEI APENAS
          <br />
          UM SISTEMA.
        </div>

        <div
          ref={title2Ref}
          className="font-syncopate uppercase leading-[1.05] tracking-[0.02em] mt-4"
          style={{
            fontSize: 'clamp(2rem, 5.5vw, 5rem)',
            background: 'linear-gradient(135deg, #7a00ff 0%, #00f0ff 50%, #ff00c8 100%)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            backgroundClip: 'text',
            filter: 'drop-shadow(0 0 40px rgba(122,0,255,0.4))',
          }}
        >
          EU CRIEI UMA MENTE.
        </div>

        <p
          ref={subtitleRef}
          className="mt-8 text-lg max-w-xl mx-auto"
          style={{ color: '#94a3b8' }}
        >
          A inteligência artificial multimodal criada por{' '}
          <span className="text-white font-semibold">Cris Oliveira</span>.
          <br />
          Texto, imagem, vídeo, música e arquivos em um único fluxo de consciência.
        </p>

        <p className="mt-4 text-sm max-w-2xl mx-auto" style={{ color: '#64748b' }}>
          Arquitetura híbrida em operação: Aurora Web, backend remoto modular e integração viva.
        </p>

        {/* Energy Pills */}
        <div className="flex flex-wrap justify-center gap-3 mt-10">
          {['Local + Cloud', 'Operacional', 'Adaptativa'].map((tag) => (
            <span
              key={tag}
              className="px-5 py-2 text-xs font-semibold uppercase tracking-[0.1em] rounded-pill border border-[#7a00ff]/30"
              style={{
                background: 'rgba(122, 0, 255, 0.08)',
                color: '#00f0ff',
                boxShadow: '0 0 15px rgba(122, 0, 255, 0.15)',
              }}
            >
              {tag}
            </span>
          ))}
        </div>

        {/* Scroll Indicator */}
        <div className="mt-20 flex flex-col items-center gap-2 opacity-60">
          <div className="w-[1px] h-10 bg-gradient-to-b from-[#00f0ff]/60 to-transparent" />
        </div>
      </div>
    </section>
  );
}
